
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FieldError } from "../FieldError";

interface PropertyStructuralDetailsProps {
  foundationType: string;
  setFoundationType: (value: string) => void;
  yearBuilt: string;
  setYearBuilt: (value: string) => void;
  lotSize: string;
  setLotSize: (value: string) => void;
  architecture: string;
  setArchitecture: (value: string) => void;
  parking: string;
  setParking: (value: string) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyStructuralDetailsSection = ({
  foundationType,
  setFoundationType,
  yearBuilt,
  setYearBuilt,
  lotSize,
  setLotSize,
  architecture,
  setArchitecture,
  parking,
  setParking,
  errors = {},
  clearFieldError,
}: PropertyStructuralDetailsProps) => {
  const foundationErr = errors.foundationType;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Label htmlFor="foundationType">Foundation Type*</Label>
        <Select
          value={foundationType}
          onValueChange={(v) => { setFoundationType(v); clearFieldError?.("foundationType"); }}
          required
        >
          <SelectTrigger
            id="foundationType"
            aria-invalid={!!foundationErr}
            className={cn("bg-muted", foundationErr && "border-destructive focus-visible:ring-destructive")}
          >
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Slab">Slab</SelectItem>
            <SelectItem value="Crawl Space">Crawl Space</SelectItem>
            <SelectItem value="Basement">Basement</SelectItem>
          </SelectContent>
        </Select>
        <FieldError message={foundationErr} />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="yearBuilt">Year Built</Label>
        <Input 
          id="yearBuilt" 
          type="number"
          placeholder="1990" 
          className="bg-muted"
          min="1800"
          max={new Date().getFullYear()}
          value={yearBuilt}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (/^\d{0,4}$/.test(value) && parseInt(value) <= new Date().getFullYear())) {
              setYearBuilt(value);
            }
          }}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="lotSize">Lot Size (Sq. Ft.)</Label>
        <Input 
          id="lotSize" 
          placeholder="5000" 
          className="bg-muted"
          value={lotSize}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            const formatted = value ? parseInt(value).toLocaleString() : '';
            setLotSize(formatted);
          }} 
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="architecture">Architecture Style</Label>
        <Select
          value={architecture}
          onValueChange={setArchitecture}
        >
          <SelectTrigger id="architecture" className="bg-muted">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="colonial">Colonial</SelectItem>
            <SelectItem value="ranch">Ranch</SelectItem>
            <SelectItem value="cape-cod">Cape Cod</SelectItem>
            <SelectItem value="bungalow">Bungalow</SelectItem>
            <SelectItem value="tudor">Tudor</SelectItem>
            <SelectItem value="victorian">Victorian</SelectItem>
            <SelectItem value="craftsman">Craftsman</SelectItem>
            <SelectItem value="modern">Modern</SelectItem>
            <SelectItem value="mediterranean">Mediterranean</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="parking">Parking Information</Label>
        <Select
          value={parking}
          onValueChange={setParking}
        >
          <SelectTrigger id="parking" className="bg-muted">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="garage-1">1 Car Garage</SelectItem>
            <SelectItem value="garage-2">2 Car Garage</SelectItem>
            <SelectItem value="garage-3">3+ Car Garage</SelectItem>
            <SelectItem value="carport">Carport</SelectItem>
            <SelectItem value="driveway">Driveway</SelectItem>
            <SelectItem value="parking-slab">Parking Slab</SelectItem>
            <SelectItem value="street">Street</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
